import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import { formatInt, formatMoney } from '../daily-brief/format';
import {
  domainFor,
  nearestIndex,
  polyline,
  type ChartLayout,
} from './chartGeometry';
import type { MasterOverlayModel, OverlayMetricId } from './types';

const LAYOUT: ChartLayout = {
  width: 960,
  height: 320,
  padL: 52,
  padR: 52,
  padT: 16,
  padB: 36,
};

function formatMetric(
  id: OverlayMetricId,
  v: number | null | undefined,
  currency?: string,
): string {
  if (v == null) return 'N/A';
  if (id === 'gmv' || id === 'aov' || id === 'asp') return formatMoney(v, currency);
  if (id === 'health' || id === 'confidence') return String(Math.round(v));
  return formatInt(v);
}

export function OverlayChart({
  model,
  enabled,
  compare,
  connectionId,
}: {
  model: MasterOverlayModel;
  enabled: OverlayMetricId[];
  compare: boolean;
  connectionId: string | null;
}) {
  const navigate = useNavigate();
  const uid = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [focusIdx, setFocusIdx] = useState(0);

  const axisOf = useMemo(() => {
    const map = new Map(model.metrics.map((m) => [m.id, m.axis] as const));
    return (id: OverlayMetricId) => map.get(id) ?? 'count';
  }, [model.metrics]);

  const colorOf = useMemo(() => {
    const map = new Map(model.metrics.map((m) => [m.id, m.color] as const));
    return (id: OverlayMetricId) => map.get(id) ?? 'var(--shell-accent)';
  }, [model.metrics]);

  const moneyDomain = domainFor(model.points, enabled, compare, 'money', axisOf);
  const countDomain = domainFor(model.points, enabled, compare, 'count', axisOf);
  const scoreDomain = domainFor(model.points, enabled, compare, 'score', axisOf);

  const n = model.points.length;
  const activeIdx = hover ?? focusIdx;
  const active = n > 0 ? model.points[Math.min(activeIdx, n - 1)] : null;

  const onPointer = (clientX: number, rect: DOMRect) => {
    const localX = ((clientX - rect.left) / rect.width) * LAYOUT.width;
    setHover(nearestIndex(localX, LAYOUT, n));
  };

  const openDay = (iso: string) => {
    if (!connectionId) {
      navigate('/amazon/connect');
      return;
    }
    navigate(
      `/amazon/orders?connection=${encodeURIComponent(connectionId)}&from=${iso}&to=${iso}`,
    );
  };

  if (!n) {
    return (
      <p className={`py-12 text-center text-sm ${shell.muted}`}>
        No day buckets for this period. Link a connection with LIVE orders to plot the overlay.
      </p>
    );
  }

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${LAYOUT.width} ${LAYOUT.height}`}
        className="h-auto w-full max-h-[360px]"
        role="img"
        aria-labelledby={`${uid}-title ${uid}-desc`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            setFocusIdx((i) => Math.min(n - 1, i + 1));
            setHover(null);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setFocusIdx((i) => Math.max(0, i - 1));
            setHover(null);
          } else if (e.key === 'Enter' && active) {
            openDay(active.iso);
          }
        }}
        onPointerMove={(e) => onPointer(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerLeave={() => setHover(null)}
        onClick={() => active && openDay(active.iso)}
        style={{ touchAction: 'manipulation' }}
      >
        <title id={`${uid}-title`}>Master business overlay analytics</title>
        <desc id={`${uid}-desc`}>
          Multi-metric trend for {model.periodLabel}. Use arrow keys to inspect days; Enter to open
          Orders.
        </desc>

        {/* grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = LAYOUT.padT + (LAYOUT.height - LAYOUT.padT - LAYOUT.padB) * (1 - t);
          return (
            <line
              key={t}
              x1={LAYOUT.padL}
              x2={LAYOUT.width - LAYOUT.padR}
              y1={y}
              y2={y}
              stroke="var(--shell-border)"
              strokeWidth={1}
              opacity={0.7}
            />
          );
        })}

        {/* axes labels */}
        <text
          x={12}
          y={LAYOUT.padT + 8}
          fill="var(--shell-fg-subtle)"
          fontSize={10}
          className="tabular-nums"
        >
          {formatAxisTop(moneyDomain)}
        </text>
        <text
          x={LAYOUT.width - 8}
          y={LAYOUT.padT + 8}
          textAnchor="end"
          fill="var(--shell-fg-subtle)"
          fontSize={10}
          className="tabular-nums"
        >
          count / score
        </text>

        {enabled.map((id) => {
          const axis = axisOf(id);
          const yDomain =
            axis === 'money' ? moneyDomain : axis === 'score' ? scoreDomain : countDomain;
          const pts = polyline(model.points, id, LAYOUT, n, yDomain, false);
          const priorPts =
            compare && model.priorLabel
              ? polyline(model.points, id, LAYOUT, n, yDomain, true)
              : '';
          return (
            <g key={id}>
              {priorPts && (
                <polyline
                  fill="none"
                  stroke={colorOf(id)}
                  strokeWidth={1.25}
                  strokeDasharray="4 3"
                  opacity={0.45}
                  points={priorPts}
                  pointerEvents="none"
                />
              )}
              {pts && (
                <polyline
                  fill="none"
                  stroke={colorOf(id)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={pts}
                  className="motion-safe:transition-opacity duration-[var(--shell-motion-fast)]"
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}

        {/* cursor */}
        {active && (
          <line
            x1={
              n <= 1
                ? LAYOUT.padL + (LAYOUT.width - LAYOUT.padL - LAYOUT.padR) / 2
                : LAYOUT.padL +
                  (active.index / (n - 1)) * (LAYOUT.width - LAYOUT.padL - LAYOUT.padR)
            }
            x2={
              n <= 1
                ? LAYOUT.padL + (LAYOUT.width - LAYOUT.padL - LAYOUT.padR) / 2
                : LAYOUT.padL +
                  (active.index / (n - 1)) * (LAYOUT.width - LAYOUT.padL - LAYOUT.padR)
            }
            y1={LAYOUT.padT}
            y2={LAYOUT.height - LAYOUT.padB}
            stroke="var(--shell-fg-muted)"
            strokeWidth={1}
            strokeDasharray="2 2"
            opacity={0.6}
            pointerEvents="none"
          />
        )}

        {/* x labels few */}
        {model.points
          .filter((_, i) => i === 0 || i === n - 1 || i === Math.floor(n / 2))
          .map((p) => {
            const i = p.index;
            const x =
              n <= 1
                ? LAYOUT.padL + (LAYOUT.width - LAYOUT.padL - LAYOUT.padR) / 2
                : LAYOUT.padL + (i / (n - 1)) * (LAYOUT.width - LAYOUT.padL - LAYOUT.padR);
            return (
              <text
                key={p.iso}
                x={x}
                y={LAYOUT.height - 10}
                textAnchor="middle"
                fill="var(--shell-fg-subtle)"
                fontSize={10}
              >
                {p.iso.slice(5)}
              </text>
            );
          })}
      </svg>

      {active && (
        <div
          role="status"
          className={`mt-2 rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} px-3 py-2 text-xs`}
        >
          <p className="font-medium text-[var(--shell-fg)]">
            {active.label}{' '}
            <span className={shell.muted}>({active.iso})</span>
            {compare && active.priorIso && (
              <span className={`${shell.subtle}`}> · prior {active.priorIso}</span>
            )}
          </p>
          <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3 lg:grid-cols-4">
            {enabled.map((id) => {
              const label = model.metrics.find((m) => m.id === id)?.label ?? id;
              return (
                <li key={id} className="flex justify-between gap-2 tabular-nums">
                  <span style={{ color: colorOf(id) }}>{label}</span>
                  <span className="text-[var(--shell-fg)]">
                    {formatMetric(id, active.values[id], model.currency)}
                    {compare && (
                      <span className={`ml-1 ${shell.subtle}`}>
                        / {formatMetric(id, active.priorValues[id], model.currency)}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className={`mt-1 ${shell.subtle}`}>
            Click chart or press Enter to open Orders for this day.
          </p>
        </div>
      )}
    </div>
  );
}

function formatAxisTop(domain: [number, number]): string {
  const [, max] = domain;
  if (max >= 1_000_000) return `${(max / 1_000_000).toFixed(1)}M`;
  if (max >= 1000) return `${(max / 1000).toFixed(1)}k`;
  return max.toFixed(0);
}
